import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CORRIDORS, formatAmount } from "@/lib/constants";
import { ArrowRight, Calculator } from "lucide-react";

const PRESETS = {
  tg_ga: [5000, 25000, 50000, 100000, 250000],
  ga_fr: [10000, 50000, 100000, 250000, 500000],
  ga_rw: [5000, 25000, 50000, 100000, 250000],
  fr_ga: [20, 50, 100, 200, 500],  // amounts in EUR for FR → GA
};

// Presets expressed in the TARGET (received) currency, for reverse simulation.
const RECEIVE_PRESETS = {
  tg_ga: [5000, 25000, 50000, 100000, 250000],   // XAF
  ga_fr: [20, 50, 100, 200, 500],                // EUR
  ga_rw: [10000, 50000, 100000, 250000, 500000], // RWF
  fr_ga: [20000, 50000, 100000, 200000, 500000], // XAF
};

const MIN_BY_CORRIDOR = {
  tg_ga: 2000,
  ga_fr: 2000,
  ga_rw: 2000,
  fr_ga: 20,
};

export default function PriceCalculator() {
  const [corridor, setCorridor] = useState("tg_ga");
  const [mode, setMode] = useState("send"); // "send" (source) | "receive" (target)
  const [amount, setAmount] = useState(PRESETS[corridor][2]);
  const [receiveAmount, setReceiveAmount] = useState(RECEIVE_PRESETS[corridor][2]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Reset to a sensible preset when switching corridor
    setAmount(PRESETS[corridor][2]);
    setReceiveAmount(RECEIVE_PRESETS[corridor][2]);
  }, [corridor]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const params = new URLSearchParams({ corridor });
      if (mode === "receive") {
        const r = parseFloat(receiveAmount);
        if (!r || r <= 0) { setQuote(null); return; }
        params.set("amount_receive", String(r));
      } else {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0 || amt < (MIN_BY_CORRIDOR[corridor] || 0)) { setQuote(null); return; }
        params.set("amount_xof", String(amt));
      }
      setLoading(true);
      try {
        const { data } = await api.get(`/quote?${params.toString()}`);
        if (!cancelled) setQuote(data);
      } catch (_e) {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [amount, receiveAmount, mode, corridor]);

  const c = CORRIDORS[corridor];

  return (
    <section className="py-24 bg-[var(--sl-bg-alt)]" id="tarifs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">
            Tarifs transparents
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">
            Combien arrive <br />à destination ?
          </h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Choisissez votre corridor et le montant. Les frais et le montant reçu s'affichent instantanément.
          </p>

          <div className="mt-6 inline-flex p-1 bg-white rounded-full border border-gray-200" role="tablist" data-testid="corridor-switch">
            {Object.values(CORRIDORS).map((co) => (
              <button
                key={co.id}
                onClick={() => setCorridor(co.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  corridor === co.id ? "bg-[var(--sl-primary)] text-white" : "text-gray-700 hover:text-[var(--sl-primary)]"
                }`}
                data-testid={`corridor-tab-${co.id}`}
              >
                {co.flagFrom} {co.short} {co.flagTo}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-2">
            <div className="text-xs uppercase tracking-widest text-gray-500">Exemples rapides</div>
            <div className="flex flex-wrap gap-2">
              {(mode === "receive" ? RECEIVE_PRESETS[corridor] : PRESETS[corridor]).map((v) => {
                const active = mode === "receive" ? Number(receiveAmount) === v : Number(amount) === v;
                return (
                  <button
                    key={v}
                    onClick={() => (mode === "receive" ? setReceiveAmount(v) : setAmount(v))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      active
                        ? "bg-[var(--sl-primary)] text-white border-[var(--sl-primary)]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[var(--sl-primary)]"
                    }`}
                    data-testid={`calc-preset-${v}`}
                  >
                    {formatAmount(v, mode === "receive" ? c.target_currency : c.source_currency)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="sl-card relative overflow-hidden" data-testid="calculator-card">
            <div className="absolute top-6 right-6 text-[var(--sl-primary)]/10">
              <Calculator size={120} />
            </div>

            {/* Direction toggle: simulate by send amount OR by received amount */}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-[var(--sl-bg-alt)] border border-gray-200 mb-4 relative z-10" data-testid="calc-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("send")}
                className={`py-2 rounded-lg text-sm font-semibold transition ${mode === "send" ? "bg-white shadow-sm text-[var(--sl-primary)]" : "text-gray-500"}`}
                data-testid="calc-mode-send"
              >
                Vous envoyez ({c.source_currency})
              </button>
              <button
                type="button"
                onClick={() => setMode("receive")}
                className={`py-2 rounded-lg text-sm font-semibold transition ${mode === "receive" ? "bg-white shadow-sm text-[var(--sl-primary)]" : "text-gray-500"}`}
                data-testid="calc-mode-receive"
              >
                Montant reçu ({c.target_currency})
              </button>
            </div>

            {mode === "receive" ? (
              <>
                <label className="sl-label flex items-center justify-between">
                  <span>Le bénéficiaire reçoit ({c.target_currency})</span>
                  {loading && <span className="text-xs text-gray-400">…</span>}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={1}
                    value={receiveAmount}
                    onChange={(e) => setReceiveAmount(e.target.value)}
                    className="sl-input text-3xl font-heading font-bold pr-20"
                    data-testid="calc-receive-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-sm">
                    {c.target_currency}
                  </span>
                </div>
              </>
            ) : (
              <>
                <label className="sl-label flex items-center justify-between">
                  <span>Vous envoyez ({c.source_currency})</span>
                  {loading && <span className="text-xs text-gray-400">…</span>}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={MIN_BY_CORRIDOR[corridor] || 100}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="sl-input text-3xl font-heading font-bold pr-20"
                    data-testid="calc-amount-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-sm">
                    {c.source_currency}
                  </span>
                </div>
                {parseFloat(amount) > 0 && parseFloat(amount) < (MIN_BY_CORRIDOR[corridor] || 0) && (
                  <div className="text-xs text-red-600 mt-2" data-testid="calc-min-warning">
                    Montant minimum : {MIN_BY_CORRIDOR[corridor]} {c.source_currency}.
                  </div>
                )}
              </>
            )}

            {quote && (
              <>
                <div className="mt-5 grid grid-cols-3 gap-3">
                  <Stat label={`Frais (${quote.fee_percent}%)`} value={formatAmount(quote.fee, c.source_currency)} accent />
                  <Stat label="Taux" value={`× ${quote.exchange_rate}`} mono />
                  <Stat label="Net envoyé" value={formatAmount(quote.net_xof, c.source_currency)} />
                </div>

                <div
                  className="mt-5 p-5 rounded-2xl text-white relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, var(--sl-primary-dark), var(--sl-primary))" }}
                >
                  {mode === "receive" ? (
                    <>
                      <div className="text-xs uppercase tracking-widest text-white/70">
                        Vous devez envoyer
                      </div>
                      <div className="mt-1 font-heading text-4xl font-bold" data-testid="calc-result-send">
                        {formatAmount(quote.amount_xof, c.source_currency)}
                      </div>
                      <div className="text-xs text-white/60 mt-1">
                        Pour que le bénéficiaire reçoive {formatAmount(quote.amount_xaf, c.target_currency)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs uppercase tracking-widest text-white/70">
                        Reçu {corridor === "tg_ga" ? "au Gabon" : corridor === "ga_rw" ? "au Rwanda" : corridor === "fr_ga" ? "au Gabon" : "en France"}
                      </div>
                      <div className="mt-1 font-heading text-4xl font-bold" data-testid="calc-result-amount">
                        {formatAmount(quote.amount_xaf, c.target_currency)}
                      </div>
                      <div className="text-xs text-white/60 mt-1">
                        Pour {formatAmount(quote.amount_xof, c.source_currency)} envoyés
                      </div>
                    </>
                  )}
                </div>

                <Link
                  to={`/envoyer?corridor=${corridor}&amount=${quote.amount_xof}`}
                  className="sl-btn-primary w-full mt-5"
                  data-testid="calc-send-cta"
                >
                  Envoyer ce montant <ArrowRight size={16} />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent, mono }) {
  return (
    <div className="bg-[var(--sl-bg-alt)] rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading font-bold mt-1 ${accent ? "text-amber-700" : "text-gray-900"} ${mono ? "font-mono text-sm" : "text-lg"}`}>
        {value}
      </div>
    </div>
  );
}
